import { FormComboBox, FormController, FormInput } from '@/components/form';
import { ProcurementApi } from '@/state/api';
import { ProcurementType } from '@/types';
import { transformAddressToString } from '@/types/addressType';
import { Box, Flex, Separator, Text } from '@chakra-ui/react';
import { Drawer, DrawerTrigger, toasterApi } from '@ui';
import { countries } from 'countries-list';
import { useState } from 'react';
import { Control, FieldErrors, UseFormRegister } from 'react-hook-form';
import z from 'zod';
import { Global } from '@emotion/react';

type PostSupplierType = ProcurementType.Suppliers.Post['body'];
type PostSupplierContactType = ProcurementType.SuppliersContacts.Post['body'];

const CreateSupplier = () => {
  const countriesList = Object.values(countries);
  const [isAddSupplierDrawerOpen, setIsAddSupplierDrawerOpen] =
    useState<boolean>(false);

  // Create supplier mutation
  const [postSuppliersMutation] = ProcurementApi.Api.usePostSuppliersMutation();

  // Create supplier contact mutation
  const [postSuppliersContactsMutation] =
    ProcurementApi.Api.usePostSupplierContactsMutation();

  const handleSubmitAddSupplier = async (
    formData: AddSupplierFormSchemaType,
  ) => {
    // Format Supplier payload
    const supplierPayload: PostSupplierType = {
      name: formData?.name,
      email: formData?.email,
      phone: formData?.phone,
      notes: formData?.notes,
      address: transformAddressToString({
        street: formData?.street,
        city: formData?.city,
        state: formData?.state,
        zipCode: formData?.zipCode,
        country: formData?.country,
      }),
    };

    try {
      // Create Supplier
      const supplier = await postSuppliersMutation({
        body: supplierPayload,
      }).unwrap();
      // Format Supplier Contact payload
      const supplierContactPayload: PostSupplierContactType = {
        name: formData.supplierContactName,
        email: formData.supplierContactEmail,
        phone: formData.supplierContactPhone,
        role: formData.supplierContactRole,
        supplier: supplier?.id,
      };

      // Create Supplier Contact
      await postSuppliersContactsMutation({
        body: supplierContactPayload,
        params: {
          supplier_id: supplier?.id,
        },
      }).unwrap();

      toasterApi({
        type: 'success',
        status: 200,
        code: 'Success',
        message: 'Supplier created successfully',
      });
    } catch {
      toasterApi({
        type: 'error',
        status: 500,
        code: 'Error',
        message: 'Could not create supplier',
      });
    } finally {
      setIsAddSupplierDrawerOpen(false);
    }
  };

  return (
    <Flex>
       <Global
        styles={{
          // Chakra v3 Combobox parts
          '[data-scope="combobox"][data-part="positioner"]': {
            zIndex: '3000 !important', // puts the combobox items above
          },
          '[data-scope="combobox"][data-part="content"]': {
            zIndex: '3000 !important',
          },
        }}
      />
      <Drawer
        placement="end"
        title="Add Supplier"
        trigger={
          <DrawerTrigger>
            <Box
              cursor="pointer"
              color="fg"
              bg="terracotta.500"
              paddingY={2}
              paddingX={3}
              borderRadius="sm"
            >
              Add Supplier
            </Box>
          </DrawerTrigger>
        }
        rootProps={{
          size: 'md',
          open: isAddSupplierDrawerOpen,
          onOpenChange: (isOpen) => setIsAddSupplierDrawerOpen(isOpen.open),
        }}
      >
        <Separator marginBottom={4} />

        <Flex flexDirection="column" gapY={4}>
          <FormController
            size="lg"
            formSchema={addSupplierFormSchema}
            onFormSubmit={handleSubmitAddSupplier}
            defaultValues={{
              name: '',
              email: '',
              phone: '',
              notes: '',
              street: '',
              city: '',
              state: '',
              zipCode: '',
              country: '',
              supplierContactName: '',
              supplierContactEmail: '',
              supplierContactPhone: '',
              supplierContactRole: '',
            }}
          >
            {({
              register,
              control,
              errors,
            }: {
              register: UseFormRegister<AddSupplierFormSchemaType>;
              control: Control<AddSupplierFormSchemaType>;
              errors: FieldErrors<AddSupplierFormSchemaType>;
            }) => {
              return (
                <Flex flexDirection="column" gapY={10} overflowX="hidden">
                  <Flex flexDirection="column" gapY={1} mt={6}>
                    <Text fontSize="md" fontWeight="bold" mb={4}>
                      Supplier Details
                    </Text>
                    <Flex gapX={2}>
                      <Box width="220px">
                        <FormInput
                          control={control}
                          label="Supplier name"
                          id="name"
                          register={register}
                          errors={errors}
                          showLabelAtTop
                          width="220px"
                          placeholder="e.g. John Smith"
                          required
                        />
                      </Box>
                      <Box width="220px">
                        <FormInput
                          control={control}
                          label="Email"
                          id="email"
                          register={register}
                          errors={errors}
                          showLabelAtTop
                          width="220px"
                          placeholder="e.g. john.smith@example.com"
                          type="email"
                          required
                        />
                      </Box>
                    </Flex>
                    <Flex gapX={2}>
                      <Box width="220px">
                        <FormInput
                          control={control}
                          label="Phone"
                          id="phone"
                          register={register}
                          errors={errors}
                          showLabelAtTop
                          width="220px"
                          placeholder="e.g. (123) 456-7890"
                          type="tel"
                          required
                        />
                      </Box>
                      <Box width="220px">
                        <FormInput
                          control={control}
                          label="Notes(optional)"
                          id="notes"
                          register={register}
                          errors={errors}
                          showLabelAtTop
                          width="220px"
                          placeholder="e.g. Notes..."
                        />
                      </Box>
                    </Flex>
                    <Flex gapX={2}>
                      <Box width="220px">
                        <FormInput
                          control={control}
                          label="Street address"
                          id="street"
                          register={register}
                          errors={errors}
                          showLabelAtTop
                          width="220px"
                          placeholder="e.g. 123 Main St"
                          required
                        />
                      </Box>
                      <Box width="220px">
                        <FormInput
                          control={control}
                          label="City"
                          id="city"
                          register={register}
                          errors={errors}
                          showLabelAtTop
                          width="220px"
                          placeholder="e.g. New York"
                          required
                        />
                      </Box>
                    </Flex>
                    <Flex gapX={2}>
                      <Box width="220px">
                        <FormInput
                          control={control}
                          label="State"
                          id="state"
                          register={register}
                          errors={errors}
                          showLabelAtTop
                          width="220px"
                          placeholder="e.g. New York"
                          required
                        />
                      </Box>
                      <Box width="220px">
                        <FormInput
                          control={control}
                          label="Zip code"
                          id="zipCode"
                          register={register}
                          errors={errors}
                          showLabelAtTop
                          width="220px"
                          placeholder="e.g. 12345"
                          required
                        />
                      </Box>
                    </Flex>
                    <Flex gapX={2}>
                      <Box width="220px">
                        <FormComboBox
                          register={register}
                          control={control}
                          errors={errors}
                          id="country"
                          label="Country"
                          keyForDisplay="name"
                          keyForValue="name"
                          items={countriesList ?? []}
                          showLabelAtTop
                          required
                        />
                      </Box>
                    </Flex>
                  </Flex>
                  <Flex flexDirection="column" gapY={1}>
                    <Text fontSize="md" fontWeight="bold" mb={4}>
                      Supplier Contact Details
                    </Text>
                    <Flex gapX={2}>
                      <Box width="220px">
                        <FormInput
                          control={control}
                          label="Supplier contact name"
                          id="supplierContactName"
                          register={register}
                          errors={errors}
                          showLabelAtTop
                          width="220px"
                          placeholder="e.g. James Smith"
                          required
                        />
                      </Box>
                      <Box width="220px">
                        <FormInput
                          control={control}
                          label="Supplier contact email"
                          id="supplierContactEmail"
                          register={register}
                          errors={errors}
                          showLabelAtTop
                          width="220px"
                          placeholder="e.g. james.smith@example.com"
                          type="email"
                          required
                        />
                      </Box>
                    </Flex>
                    <Flex gapX={2}>
                      <Box width="220px">
                        <FormInput
                          control={control}
                          label="Supplier contact phone"
                          id="supplierContactPhone"
                          register={register}
                          errors={errors}
                          showLabelAtTop
                          width="220px"
                          placeholder="e.g. (123) 456-7890"
                          type="tel"
                          required
                        />
                      </Box>
                      <Box width="220px">
                        <FormInput
                          control={control}
                          label="Supplier contact role"
                          id="supplierContactRole"
                          register={register}
                          errors={errors}
                          showLabelAtTop
                          width="220px"
                          placeholder="e.g. Account Manager"
                          required
                        />
                      </Box>
                    </Flex>
                  </Flex>
                </Flex>
              );
            }}
          </FormController>
        </Flex>
      </Drawer>
    </Flex>
  );
};
export default CreateSupplier;

const addSupplierFormSchema = z.object({
  name: z.string().nonempty({ error: 'Supplier name is required' }),
  email: z.email({ error: 'Enter a valid email' }),
  phone: z.string().nonempty({ error: 'Supplier phone is required' }),
  notes: z.string().optional(),
  street: z.string().nonempty({ error: 'Street address is required' }),
  city: z.string().nonempty({ error: 'City name is required' }),
  state: z.string().nonempty({ error: 'State name is required' }),
  zipCode: z.string().nonempty({ error: 'Zip code is required' }),
  country: z.string().nonempty({ error: 'Country name is required' }),
  supplierContactName: z
    .string()
    .nonempty({ error: 'Supplier contact name is required' }),
  supplierContactEmail: z.email({
    error: 'Enter a valid email',
  }),
  supplierContactPhone: z
    .string()
    .nonempty({ error: 'Supplier contact phone is required' }),
  supplierContactRole: z
    .string()
    .nonempty({ error: 'Supplier contact role is required' }),
});
type AddSupplierFormSchemaType = z.infer<typeof addSupplierFormSchema>;
